import { IonButton, IonCheckbox, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem, IonItemDivider, IonLabel, IonModal, IonSelect, IonSelectOption, IonText, IonTextarea, IonTitle, IonToolbar } from "@ionic/react"
import { AppContext } from "context/app.context";
import { closeCircleOutline } from "ionicons/icons";
import { createDomain, generateKeys, getDomainPHQConfig, updateDomain } from "misc/phqHTTP";
import { DomainKeys, DomainPHQConfig, PHQDomain } from "models/domain.model";
import React, { useContext, useEffect, useRef, useState } from "react"
import './style.scss'

const AddEditDomain = (props: {
    mode: "add" | "edit" | "none",
    isOpen: boolean,
    domain?: PHQDomain,
    setShowModal: (value: boolean) => void,
    onAddDomain: (domain: PHQDomain) => void
    onEditDomain: (domain: PHQDomain) => void
}) => {

    const { notifySuccess } = useContext(AppContext);
    const [approvalSetting, setApprovalSetting] = useState("auto");
    const [keys, setDomainKeys] = useState<DomainKeys>();
    const [domainPHQConfig, setDomainPHQConfig] = useState<DomainPHQConfig>();
    const form = useRef<HTMLFormElement>(null);

    const getFormData = () => {
        const formData = new FormData(form.current!);
        const data = Object.fromEntries(formData) as any;

        data.DomainID = props.domain?.DomainID;
        data.IsActive = props.mode === "add" || data.IsActive === "on";
        data.SMTPPort = isNaN(data.SMTPPort) ? null : +data.SMTPPort;
        data.EnableSSL = props.mode === "add" || data.EnableSSL === "on";
        data.SMTPAuth = props.mode === "add" || data.SMTPAuth === "on";
        data.ApprovalSetting = approvalSetting;

        Object.entries(data).forEach(e => {
            const [key, value] = e;
            if(value === "") data[key] = null;
        })

        return data;
    }

    const addDomain = () => {
        const domain = getFormData();
        createDomain(domain).then(res => {
            notifySuccess("Domain added");
            props.onAddDomain(res.data.result);
            props.setShowModal(false);
        })
    }

    const editDomain = () => {
        const domain = getFormData();
        updateDomain(domain).then(res => {
            notifySuccess("Domain updated");
            props.onEditDomain(res.data.result);
            props.setShowModal(false);
        })
    }

    useEffect(() => {
        if(props.mode === 'add') {
            generateKeys().then(res => {
                setDomainKeys(res.data.result);
            })
        }
        if(props.mode === "edit") {
            getDomainPHQConfig(props.domain?.DomainID!).then(res => {
                setDomainPHQConfig(res.data.result);
            })
        }
    }, [props.mode, props.domain])

    return (
        <IonModal cssClass="add-edit-modal" isOpen={props.isOpen} onDidDismiss={_ => props.setShowModal(false)}>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonTitle>{props.mode.toUpperCase()} DOMAIN</IonTitle>
                    <IonButton slot="end" onClick={(_) => props.setShowModal(false)}>
                        <IonIcon slot="icon-only" size="large" icon={closeCircleOutline}></IonIcon>
                    </IonButton>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <form ref={form}>
                    <IonItem hidden={props.mode === "add"}>
                        <IonLabel>Is Active</IonLabel>
                        <IonCheckbox checked={!!props.domain?.IsActive} name="IsActive"></IonCheckbox>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Name <IonText color="danger">*</IonText></IonLabel>
                        <IonInput value={props.domain?.Name} name="Name"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Domain Code <IonText color="danger">*</IonText></IonLabel>
                        <IonInput value={props.domain?.Code} name="Code"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Api Key</IonLabel>
                        <IonInput value={props.mode === 'edit' ? props.domain?.ApiKey : keys?.ApiKey} name="ApiKey"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Secret</IonLabel>
                        <IonInput value={props.mode === 'edit' ? props.domain?.Secret: keys?.Secret} name="Secret"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Integration API Key</IonLabel>
                        <IonInput value={props.mode === 'edit' ? props.domain?.IntegrationAPIKey : keys?.IntegrationAPIKey} name="IntegrationAPIKey"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Integration API Virtual Directory</IonLabel>
                        <IonInput value={props.mode === 'edit' ? props.domain?.IntegrationAPIVirDir : "integrationapi" } name="IntegrationAPIVirDir"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Base URL <IonText color="danger">*</IonText></IonLabel>
                        <IonInput value={props.domain?.BaseUrl} name="BaseUrl" inputmode="url" placeholder="e.g https://client.iprocedures.com"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>PHQ Form Expires in</IonLabel>
                        <IonInput value={props.mode === 'edit' ? props.domain?.FormExpInDays : 90 } name="FormExpInDays" inputmode="numeric" placeholder="# of days"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>PHQ Form Reminder in</IonLabel>
                        <IonInput value={props.mode === 'edit' ? props.domain?.FormReminderInDays : 30 } name="FormReminderInDays" inputmode="numeric" placeholder="# of days"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Approval Settings</IonLabel>
                        <IonSelect 
                            placeholder="Select one"
                            value={props.mode === 'edit' ? props.domain?.ApprovalSetting : 90 }
                            onIonChange={e => setApprovalSetting(e.detail.value)}>
                            <IonSelectOption value="auto">Auto</IonSelectOption>
                        </IonSelect>
                    </IonItem>
                    <IonItemDivider>Twilio Settings</IonItemDivider>
                    <IonItem>
                        <IonLabel>SID</IonLabel>
                        <IonInput value={props.domain?.TwilioSID} name="TwilioSID"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Secret</IonLabel>
                        <IonInput value={props.domain?.TwilioSecret} name="TwilioSecret"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Sender Phone</IonLabel>
                        <IonInput value={props.domain?.TwilioPhoneNumber} name="TwilioPhoneNumber" inputmode="tel"></IonInput>
                    </IonItem>
                    <IonItemDivider>SMTP Settings</IonItemDivider>
                    <IonItem>
                        <IonLabel>Server Address</IonLabel>
                        <IonInput value={props.domain?.SMTPServerAddress} name="SMTPServerAddress"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Port</IonLabel>
                        <IonInput value={props.domain?.SMTPPort} name="SMTPPort" type="number" inputmode="numeric" placeholder="e.g 587"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Username</IonLabel>
                        <IonInput value={props.domain?.SMTPUsername} name="SMTPUsername"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Password</IonLabel>
                        <IonInput value={props.domain?.SMTPPassword} name="SMTPPassword"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>EnableSSL</IonLabel>
                        <IonCheckbox checked={!!props.domain?.EnableSSL} name="EnableSSL"></IonCheckbox>
                    </IonItem>
                    <IonItem>
                        <IonLabel>SMTPAuth</IonLabel>
                        <IonCheckbox checked={!!props.domain?.SMTPAuth} name="SMTPAuth"></IonCheckbox>
                    </IonItem>
                    <IonItemDivider>Email</IonItemDivider>
                    <IonItem>
                        <IonLabel>Display Name</IonLabel>
                        <IonInput value={props.domain?.EmailDisplayName} name="EmailDisplayName"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>From Address</IonLabel>
                        <IonInput value={props.domain?.EmailFromAddress} name="EmailFromAddress" type="email"></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel>MetaData Login</IonLabel>
                        <IonTextarea value={props.domain?.MetaData} name="MetaData"></IonTextarea>
                    </IonItem>
                    <IonItem>
                        <IonLabel>Token Url External</IonLabel>
                        <IonInput value={props.domain?.TokenUrl} name="TokenUrl"></IonInput>
                    </IonItem>
                    
                    {props.mode === "edit" &&
                        <div>
                            <IonItemDivider>PHQ Readiness Test</IonItemDivider>
                            <IonItem>
                                <IonLabel>Has Valid PHQ System Config</IonLabel>
                                <IonText>{domainPHQConfig?.HasSystemConfig ? "YES":"NO"}</IonText>
                            </IonItem>
                            <IonItem>
                                <IonLabel>Facilities Configured for PHQ</IonLabel>
                                <IonText>{
                                    domainPHQConfig?.Facilities
                                        .filter(f => f.HasValidPHQConfig)
                                        .map(f => f.Name)
                                        .join(" ")}
                                </IonText>
                            </IonItem>
                        </div>
                    }
                </form>
            </IonContent>
            <IonFooter>
                <IonButton expand="full" color="primary" onClick={() => props.mode === "add" ? addDomain() : editDomain()}><strong>Save</strong></IonButton>
            </IonFooter>
      </IonModal>
    )
}

export default AddEditDomain;